import { useState } from "react";

import { clipboard, useWindowDimensions } from "@luce/core";
import { Redirect, useNavigation } from "@luce/core/adapters/navigation";
import { Box, Button, ScreenLayout, toast, Typography } from "@luce/ui";

import { ClientAccountTypeEnum } from "../../__generated__/graphql";
import { PageHeader, ShareModal, useClientId, useClientProfile } from "../../shared";
import { BACK_TO } from "../../shared/lib";
import { useGiftCardById } from "../gqlhooks/use-gift-card-by-id";
import { GiftDetailSkeleton } from "./components/gift-detail-skeleton";
import { ReceivedGiftDetail } from "./components/received-gift-detail";
import { SentGiftDetail } from "./components/sent-gift-detail";
import type { GiftDetailProps } from "./gift-detail.types";
import { resolveGiftDetailView, shareGiftCardMessage } from "./lib";

export function GiftDetailScreen({ giftCardId, webDomain, onTrack }: GiftDetailProps) {
  const { isDesktop } = useWindowDimensions();
  const { goBack, push } = useNavigation();
  const clientId = useClientId();
  const { client } = useClientProfile();
  const { giftCard, loading, error, refetch } = useGiftCardById(giftCardId);
  const [shareOpen, setShareOpen] = useState(false);

  if (!giftCardId || (!loading && !error && !giftCard)) {
    return <Redirect to="/profile/gift-card/history" replace />;
  }

  const view = giftCard ? resolveGiftDetailView(giftCard, clientId) : null;
  const isCorporate = client?.accountType === ClientAccountTypeEnum.Corporate;
  const title = view === "sent" ? "Order Details" : "Gift Card Details";

  const onViewInvoice = () => {
    if (giftCard?.invoice) push(`/profile/invoices/${giftCard.invoice.id}`);
  };

  const onCopyCode = async () => {
    if (!giftCard) return;
    const result = await clipboard.copy(giftCard.code);
    if (result.success) toast.success("Gift code copied");
    else toast.error("Failed to copy code");
  };

  const onShare = () => {
    setShareOpen(true);
    onTrack({ name: "initiate_share_gift_card" });
  };

  let content;
  if (!giftCard && error) {
    content = (
      <Box className="gap-spacing-md py-spacing-xl flex flex-col items-center">
        <Typography variant="body-md" color="neutral-foreground-intermediate">
          Something went wrong loading this gift card.
        </Typography>
        <Button variant="secondary" size="md" label="Retry" onClick={() => refetch()} />
      </Box>
    );
  } else if (loading || !giftCard) {
    content = <GiftDetailSkeleton />;
  } else if (view === "sent") {
    content = (
      <Box className="md:border-luce-neutral-border-mediumEmphasis md:rounded-2xl md:border">
        <SentGiftDetail
          giftCard={giftCard}
          isCorporate={isCorporate}
          onCopyCode={onCopyCode}
          onShare={onShare}
          onViewInvoice={onViewInvoice}
        />
      </Box>
    );
  } else {
    content = (
      <Box className="p-spacing-lg md:px-0">
        <ReceivedGiftDetail
          giftCard={giftCard}
          redeemed={view === "received-redeemed"}
          onCheckCreditAccount={() => push("/profile/credit-account")}
        />
      </Box>
    );
  }

  return (
    <ScreenLayout>
      <ScreenLayout.Header title={title} onBack={() => goBack(BACK_TO.giftCards)} variant="white" />

      <ScreenLayout.Body scrollable>
        <Box className="gap-spacing-lg container flex flex-col">
          {isDesktop && <PageHeader onBack={() => goBack(BACK_TO.giftCards)} title={title} />}
          {content}
        </Box>
      </ScreenLayout.Body>

      {giftCard && (
        <ShareModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          message={shareGiftCardMessage(giftCard.code, webDomain)}
          onShare={(type) => onTrack({ name: "share_gift_card", values: { type } })}
        />
      )}
    </ScreenLayout>
  );
}
